import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import { useView } from "../context/ViewContext";
import { clearAuthStorage, pruneInvalidAuth } from "../utils/auth";
import { getPostAuthRedirectPath } from "../utils/authRedirect";
import { getDefaultViewForAuthPayload } from "../utils/roleCapabilities";

const SESSION_EXPIRED_NOTICE = "Session expired, please sign in again.";
const SESSION_CHECK_FAILED_NOTICE =
  "We couldn't check your session just now. Sign in again, or carry on to your dashboard.";

const AuthPageGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setCurrentView } = useView();
  const [authLoading, setAuthLoading] = useState(true);
  const [noticeMessage, setNoticeMessage] = useState("");
  const [manualRedirectPath, setManualRedirectPath] = useState("");

  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("next");
  }, [location.search]);

  // Set by the professional-account gate (design 5a), the one place in the app
  // that sends an already-signed-in person to /signup on purpose: they are
  // adding a business account alongside the one they are signed in as.
  const isAddAccount = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("addAccount") === "1";
  }, [location.search]);

  // The session check runs once per mount (see below), but it still needs the
  // current values when it resolves.
  const latest = useRef({ navigate, setCurrentView, nextPath, isAddAccount });
  useEffect(() => {
    latest.current = { navigate, setCurrentView, nextPath, isAddAccount };
  });

  useEffect(() => {
    let isMounted = true;

    const finishAuthCheck = ({ notice = "", manualPath = "" } = {}) => {
      if (!isMounted) return;
      setNoticeMessage(notice);
      setManualRedirectPath(manualPath);
      setAuthLoading(false);
    };

    const checkSession = async () => {
      const auth = pruneInvalidAuth();
      const token = auth?.token;

      // Leave a deliberate second-account signup on the form. Bouncing it to
      // the role home -- correct for someone who merely re-opened a bookmarked
      // login page -- would make the gate's primary button a no-op.
      if (!token || latest.current.isAddAccount) {
        finishAuthCheck();
        return;
      }

      try {
        const response = await axios.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
          // The stored workspace id can belong to a previous account on this
          // browser, and /api/auth/me 403s for a workspace the user is not in.
          // This check only needs to know whether the token itself is good.
          skipActiveBusinessHeader: true,
        });

        if (!isMounted) return;

        const refreshedUser = response?.data?.user || {};
        const role = refreshedUser.role || auth?.payload?.role;
        if (!role) {
          finishAuthCheck();
          return;
        }

        const refreshedAuthProfile = {
          ...auth?.payload,
          role,
          platformRole: refreshedUser.platformRole || auth?.payload?.platformRole,
          businessWorkspaceAccess: Array.isArray(refreshedUser.businesses) && refreshedUser.businesses.length > 0,
        };
        latest.current.setCurrentView(getDefaultViewForAuthPayload(refreshedAuthProfile));
        const destination = getPostAuthRedirectPath({
          role,
          platformRole: refreshedAuthProfile.platformRole,
          nextPath: latest.current.nextPath,
          user: refreshedUser,
        });
        latest.current.navigate(destination, { replace: true });
      } catch (error) {
        if (!isMounted) return;

        const statusCode = error?.response?.status;
        // Only a 401 means the token is dead. A 403 used to be treated the same
        // way, which signed people out of a perfectly valid session whenever the
        // request carried a stale workspace id.
        if (statusCode === 401) {
          clearAuthStorage();
          latest.current.setCurrentView("customer");
          finishAuthCheck({ notice: SESSION_EXPIRED_NOTICE });
          return;
        }

        finishAuthCheck({
          notice: SESSION_CHECK_FAILED_NOTICE,
          manualPath: getPostAuthRedirectPath({
            role: auth?.payload?.role,
            nextPath: latest.current.nextPath,
          }),
        });
      }
    };

    checkSession();

    return () => {
      isMounted = false;
    };
    // Once per mount. `navigate` changes identity with the pathname in
    // react-router 6.26, so listing it re-ran the whole check — and re-hit
    // /api/auth/me — on every in-app navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authLoading) {
    return (
      <div className="auth-page">
        <div className="auth-inner">
          <p className="helper-text" role="status">
            Checking session...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {noticeMessage && (
        <p
          className="helper-text error"
          role="status"
          style={{ maxWidth: "640px", margin: "12px auto 0", textAlign: "center" }}
        >
          {noticeMessage}
        </p>
      )}
      {manualRedirectPath && (
        <div style={{ maxWidth: "640px", margin: "12px auto 0", textAlign: "center" }}>
          <button
            type="button"
            className="auth-submit"
            onClick={() => navigate(manualRedirectPath, { replace: true })}
          >
            Go to dashboard
          </button>
        </div>
      )}
      {children}
    </>
  );
};

export default AuthPageGuard;
